import { type FormEvent, useRef, useState } from "react";
import {
  Box,
  Flex,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
} from "@chakra-ui/react";
import { Camera, ImagePlus, Search } from "lucide-react";
import { useNavigate } from "react-router";
import { brandColors, radii } from "@core/theme";
import { homeCopy } from "@modules/catalog/copy";
import { useHeaderVisualSearch } from "./useHeaderVisualSearch";

export const HeaderSearch = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const visual = useHeaderVisualSearch();
  const inputRef = useRef<HTMLInputElement>(null);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const q = query.trim();
    void navigate(q === "" ? "/search" : `/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <>
      <Flex
        as="form"
        role="search"
        data-testid="header-search"
        onSubmit={submit}
        align="center"
        w="100%"
        maxW="100%"
        h={{ base: "42px", md: "46px", xl: "48px" }}
        pl={{ base: 3, md: 4 }}
        pr={1}
        gap={{ base: 1, md: 2 }}
        borderRadius={`${radii.lg}px`}
        bg="white"
        boxShadow="0 2px 12px rgba(0,0,0,0.15)"
        borderWidth="1px"
        borderColor={brandColors.border[300]}
        overflow="hidden"
        position="relative"
        zIndex={1}
        isolation="isolate"
        onClick={() => inputRef.current?.focus()}
      >
        <Box flexShrink={0} lineHeight={0}>
          <Search size={20} color={brandColors.primary[600]} strokeWidth={2} />
        </Box>
        <Input
          ref={inputRef}
          flex={1}
          minW={0}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={homeCopy.searchPlaceholder}
          variant="unstyled"
          color={brandColors.gray[900]}
          fontSize={{ base: "xs", sm: "sm", md: "md" }}
          _placeholder={{ color: brandColors.gray[400] }}
          aria-label="Search products"
        />
        <Menu placement="bottom-end" isLazy>
          <MenuButton
            as={IconButton}
            aria-label="Search by photo"
            icon={
              visual.isUploading ? (
                <Spinner size="sm" color={brandColors.primary[500]} />
              ) : (
                <Camera size={18} color={brandColors.gray[600]} />
              )
            }
            variant="ghost"
            size="sm"
            flexShrink={0}
            borderRadius="full"
            onClick={(event) => event.stopPropagation()}
          />
          <MenuList minW="180px" zIndex={20}>
            <MenuItem icon={<Camera size={16} />} onClick={() => visual.openPicker("camera")}>
              Take photo
            </MenuItem>
            <MenuItem icon={<ImagePlus size={16} />} onClick={() => visual.openPicker("gallery")}>
              From gallery
            </MenuItem>
          </MenuList>
        </Menu>
        <Flex
          as="button"
          type="submit"
          align="center"
          gap={1}
          h="38px"
          px={4}
          mr={0.5}
          borderRadius={`${radii.md}px`}
          bg={brandColors.primary[600]}
          color="white"
          fontSize="sm"
          fontWeight="600"
          flexShrink={0}
          _hover={{ bg: brandColors.primary[700] }}
        >
          <Search size={16} />
          <Box as="span" display={{ base: "none", sm: "inline" }}>
            Search
          </Box>
        </Flex>
      </Flex>
      <input
        ref={visual.cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={visual.onFileChange}
        hidden
      />
      <input
        ref={visual.galleryInputRef}
        type="file"
        accept="image/*"
        onChange={visual.onFileChange}
        hidden
      />
    </>
  );
};
